// Renders a "Networks" category section (mirroring AddressSearchInput's header +
// rows convention) above the token list when a search matches a network name.
import { RefObject } from 'react'

import { NetworkDisplay } from '~/components/component-library'
import { RelayNetworkMetadata } from '~/hooks/useRelayNetworks'
import { cn } from '~/utils/tailwind-utils'

export type NetworkMatch =
  | { kind: 'active'; chainId: number }
  | { kind: 'relay'; metadata: RelayNetworkMetadata }

type TokenSelectorNetworkMatchesProps = {
  matches: NetworkMatch[]
  highlightedIndex: number
  highlightedRef: RefObject<HTMLDivElement | null>
  highlightText?: string
  onSelect: (match: NetworkMatch) => void
  onHighlight: (index: number) => void
}

export function TokenSelectorNetworkMatches({
  matches,
  highlightedIndex,
  highlightedRef,
  highlightText,
  onSelect,
  onHighlight,
}: TokenSelectorNetworkMatchesProps) {
  if (matches.length === 0) return null

  return (
    <div role="listbox" aria-label="Network matches">
      <div className="flex justify-between px-2 py-1.5 text-xs text-secondary">
        Networks
      </div>
      {matches.map((match, index) => {
        const chainId =
          match.kind === 'active' ? match.chainId : match.metadata.id
        const isHighlighted = highlightedIndex === index

        return (
          <div
            key={`network-match-${match.kind}-${chainId}`}
            ref={isHighlighted ? highlightedRef : undefined}
            role="option"
            aria-selected={isHighlighted}
            className={cn(
              'flex w-full cursor-pointer items-center gap-2 rounded p-2 py-1.5',
              isHighlighted && 'bg-surface-muted',
            )}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => onSelect(match)}
            onMouseEnter={() => onHighlight(index)}
          >
            <NetworkDisplay
              layout="standard"
              size="small"
              chainId={chainId}
              highlightText={highlightText}
            />
          </div>
        )
      })}
    </div>
  )
}
